<script lang="ts">
	import * as pdfjsLib from 'pdfjs-dist'
	import { OverlayScrollbar } from '@repo/commons/components'
	import { writable } from 'svelte/store'
	import { onDestroy, tick } from 'svelte'
	import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

	if (typeof window !== 'undefined') {
		pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker
	}

	interface Props {
		url: string
		totalPages: number
		currentPage: number
		defaultPage?: number
		scale: number
	}

	let {
		url,
		totalPages = $bindable(1),
		currentPage = $bindable(1),
		defaultPage = 1,
		scale = $bindable(1.0)
	}: Props = $props()

	$inspect('currentPagecurrentPagecurrentPage', currentPage)

	let containerEl: HTMLDivElement | null = null
	let wrapperEl: HTMLDivElement | null = null

	const isRendering = writable(false)
	let pdfDoc: any = null
	let observer: IntersectionObserver | null = null

	const BASE_INTERSECTION_THRESHOLD = 0.5
	const BASE_WIDTH = 596
	const BASE_HEIGHT = 842

	const intersectionThreshold = $derived.by(() => {
		if (scale >= 1) {
			return BASE_INTERSECTION_THRESHOLD * (1 / scale)
		} else {
			return 1
		}
	})

	async function renderPdf(url: string) {
		if (!containerEl) return
		cleanupObserver()
		containerEl.innerHTML = ''
		currentPage = defaultPage ?? 1

		try {
			isRendering.set(true)
			pdfDoc = await pdfjsLib.getDocument(url).promise
			totalPages = pdfDoc.numPages

			await renderAllPages()
			await tick()

			// First, scroll to default page
			if (defaultPage && defaultPage > 1) {
				scrollToPage(defaultPage)
				await new Promise((r) => setTimeout(r, 250))
			}
			// then start tracking
			setupScrollTracking()
		} catch (err) {
			console.error('Error rendering PDF:', err)
		} finally {
			isRendering.set(false)
		}
	}

	async function renderAllPages() {
		if (!pdfDoc || !containerEl) return
		cleanupObserver()
		containerEl.innerHTML = ''

		for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
			const page = await pdfDoc.getPage(pageNum)
			const viewport = page.getViewport({ scale })

			const canvas = document.createElement('canvas')
			const context = canvas.getContext('2d')!
			canvas.width = viewport.width
			canvas.height = viewport.height

			const wrapper = document.createElement('div')
			wrapper.className = 'pdf-page-wrapper'
			wrapper.dataset.page = String(pageNum)
			wrapper.style.width = `${BASE_WIDTH * scale}px`
			wrapper.style.minHeight = `${BASE_HEIGHT * scale}px`

			wrapper.appendChild(canvas)
			containerEl.appendChild(wrapper)

			await page.render({ canvasContext: context, viewport }).promise
		}

		setupScrollTracking()
	}

	function cleanupObserver() {
		if (observer) {
			observer.disconnect()
			observer = null
		}
	}

	function setupScrollTracking() {
		if (!wrapperEl) return
		cleanupObserver()

		observer = new IntersectionObserver(
			(entries) => {
				const visible = entries
					.filter((e) => e.isIntersecting)
					.sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
				if (visible) {
					currentPage = Number(visible.target.dataset.page)
				}
			},
			{ root: wrapperEl, threshold: intersectionThreshold }
		)

		wrapperEl.querySelectorAll('.pdf-page-wrapper').forEach((page) => observer!.observe(page))
	}

	async function scrollToDefault(page: number) {
		await tick()

		setTimeout(() => {
			scrollToPage(page)
		}, 500)
	}

	export function scrollToPage(pageNum: number, behaviour: 'smooth' | 'instant' = 'smooth'): void {
		if (!containerEl) return
		const el = containerEl.querySelector(`.pdf-page-wrapper[data-page="${pageNum}"]`)
		if (el) el.scrollIntoView({ behavior: behaviour || 'smooth', block: 'start' })
	}

	export function zoomIn() {
		if (scale < 3.0) {
			scale = Math.round((scale + 0.25) * 100) / 100
			renderAllPages()
		}
	}

	export function zoomOut() {
		if (scale > 0.5) {
			scale = Math.round((scale - 0.25) * 100) / 100
			renderAllPages()
		}
	}

	$effect(() => {
		if (url) renderPdf(url)
	})

	$effect(() => {
		if (!defaultPage || !url || isRendering) return

		scrollToDefault(defaultPage)
	})

	onDestroy(() => cleanupObserver())
</script>

<div class="w-full">
	<OverlayScrollbar bind:containerRef={wrapperEl}>
		<div bind:this={containerEl} class="pdf-container"></div>
	</OverlayScrollbar>
</div>

<style>
	.pdf-container {
		@apply flex flex-col gap-4 items-center w-full py-7;
	}
	:global(.pdf-page-wrapper) {
		@apply relative border border-[#002C4D33] bg-white rounded-sm overflow-hidden transition-all duration-300 ease-in-out;
		scroll-margin-top: 24px;
	}
	canvas {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
	}
</style>
